import DefaultLink from '@/components/common/default/DefaultLink';
import { IndexBlogPostsCarouselProps } from '@/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import IndexBlogPostCard from './IndexBlogPostCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions, Swiper as SwiperType } from 'swiper/types';

export default function IndexBlogPostsCarousel({
  title,
  blogPosts,
  browserLocale,
}: IndexBlogPostsCarouselProps) {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  // Update navigation when refs or swiper change
  const updateNavigation = useCallback(() => {
    if (!swiper || !prevRef.current || !nextRef.current) return;

    // Only update if navigation is available
    if (swiper.navigation && typeof swiper.navigation.update === 'function') {
      // Set navigation elements
      swiper.params.navigation = {
        prevEl: prevRef.current,
        nextEl: nextRef.current,
      } as NavigationOptions;

      // Update swiper navigation
      swiper.navigation.update();
    }
  }, [swiper]);

  // Call updateNavigation when swiper or refs change
  useEffect(() => {
    if (swiper) {
      updateNavigation();
    }
  }, [swiper, updateNavigation]);

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 max-nine:tw-px-4">
        <h2 className="tw-relative tw-w-fit tw-font-plusJakartaSans tw-text-2xl tw-font-semibold tw-text-text max-nine:tw-text-lg">
          {title ?? translateCommon(CommonEnum['spiritory_magazine'])}
          <hr
            aria-hidden="true"
            className="tw-absolute tw-mt-1 tw-h-[5px] tw-w-[80%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"
          />
        </h2>
        <div className="tw-flex tw-items-center tw-justify-end">
          <DefaultLink
            href="/blog"
            className="tw-font-plusJakartaSans tw-text-base tw-font-semibold tw-text-textBlack max-nine:tw-text-sm"
          >
            {translateCommon(CommonEnum['view_more'])}
          </DefaultLink>
        </div>
      </div>
      <div className="tw-relative tw-w-full max-nine:tw-pl-4">
        <Swiper
          modules={[Navigation]}
          breakpoints={{
            0: {
              slidesPerView: 1.2,
              spaceBetween: 10,
            },
            552: {
              slidesPerView: 2,
              spaceBetween: 15,
            },
            694: {
              slidesPerView: 2.5,
              spaceBetween: 20,
            },
            856: {
              slidesPerView: 3,
              spaceBetween: 25,
            },
            1024: {
              slidesPerView: 3.5,
              spaceBetween: 30,
            },
          }}
          loop={false}
          className="tw-h-[450px]"
          navigation={false}
          onSwiper={swiperInstance => {
            setSwiper(swiperInstance);
          }}
          onAfterInit={swiperInstance => {
            // Set initial states
            setIsBeginning(swiperInstance.isBeginning);
            setIsEnd(swiperInstance.isEnd);
          }}
          onSlideChange={swiper => {
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
        >
          {blogPosts.map(blogPost => (
            <SwiperSlide
              key={blogPost.id}
              className="tw-h-[420px] tw-max-h-[420px] max-nine:tw-h-[395px] max-nine:tw-max-h-[395px]"
            >
              <IndexBlogPostCard blogPost={blogPost} browserLocale={browserLocale} />
            </SwiperSlide>
          ))}
        </Swiper>
        {!isBeginning && (
          <button
            ref={prevRef}
            onClick={() => swiper?.slidePrev()}
            className="transition-all duration-300 tw-absolute tw-left-3 tw-top-1/2 tw-z-10 tw-flex tw-h-11 tw-w-11 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300/40 tw-bg-white/70 tw-text-main tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-main hover:tw-text-white focus-visible:tw-ring-2 focus-visible:tw-ring-main/80 max-nine:tw-hidden"
          >
            <ChevronLeft className="tw-h-5 tw-w-5" />
          </button>
        )}

        {!isEnd && (
          <button
            ref={nextRef}
            onClick={() => swiper?.slideNext()}
            className="transition-all duration-300 tw-absolute tw-right-3 tw-top-1/2 tw-z-10 tw-flex tw-h-11 tw-w-11 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300/40 tw-bg-white/70 tw-text-main tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-main hover:tw-text-white focus-visible:tw-ring-2 focus-visible:tw-ring-main/80 max-nine:tw-hidden"
          >
            <ChevronRight className="tw-h-5 tw-w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
